import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Placeholder,
  Spinner,
} from "react-bootstrap";
import { HeadPage } from "src/componentes";
import MDFeVisualizaCode from "./MDFe.Visualiza.Code";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDownload,
  faPrint,
  faTruck,
  faRoute,
  faUsers,
  faBoxes,
  faFileInvoice,
  faDollarSign,
  faInfoCircle,
  faShieldAlt,
  faCreditCard,
  faIdCard,
} from "@fortawesome/free-solid-svg-icons";
import { EStatusEnvioMDFe } from "../MDFe.Dto";

const MDFeVisualizaView = () => {
  const code = MDFeVisualizaCode();
  const mdfe = code.mdfe;

  if (code.loading) {
    return (
      <Container className="py-4">
        <HeadPage headText="Visualizar MDF-e" />
        <Placeholder animation="glow" className="mt-4">
          <Placeholder xs={12} size="lg" className="mb-3" />
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={12} className="mb-2" />
        </Placeholder>
      </Container>
    );
  }

  if (!mdfe) {
    return (
      <Container className="py-4">
        <HeadPage headText="Visualizar MDF-e" />
        <Card className="mt-4 text-center p-5 border shadow-sm">
          <Card.Body>
            <h4 className="text-body mb-3">MDF-e não encontrado</h4>
            <p className="text-body-secondary mb-4">
              Não foi possível carregar as informações deste Manifesto de Documentos Fiscais.
            </p>
            <Button variant="outline-primary" onClick={code.onVoltar}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar para Listagem
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const codigoStatusEnvio =
    mdfe.autorizacao?.codigoStatusEnvio ??
    (mdfe as any).codigoStatusEnvio ??
    EStatusEnvioMDFe.Pendente;
  const isAutorizado =
    codigoStatusEnvio === EStatusEnvioMDFe.Autorizado ||
    mdfe.autorizacao?.codigoStatusResposta === 100;
  const visual = code.getVisualStatusMDFe(
    codigoStatusEnvio,
    mdfe.autorizacao?.codigoStatusResposta
  );

  const valorCargaFormatado =
    mdfe.valorTotalCarga !== undefined &&
    mdfe.valorTotalCarga !== null &&
    !isNaN(Number(mdfe.valorTotalCarga))
      ? Number(mdfe.valorTotalCarga).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0,00";

  return (
    <Container className="pb-5 container-print">
      {/* Cabeçalho superior da página */}
      <div className="no-print">
        <HeadPage headText={`MDF-e: ${mdfe.numeroMDFe} / Série: ${mdfe.serie}`} />

        {/* Barra de Ações Rápidas */}
        <Card className="my-3 border shadow-sm">
          <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3">
            <Button variant="outline-secondary" size="sm" onClick={code.onVoltar}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar
            </Button>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={code.onBaixarXml}
                disabled={code.downloadingXml || !mdfe.id}
              >
                {code.downloadingXml ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                )}
                Baixar XML
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={code.onImprimir}
                disabled={code.printing || !mdfe.id}
              >
                {code.printing ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <FontAwesomeIcon icon={faPrint} className="me-2" />
                )}
                Imprimir DAMDFE
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* 1. Status da Autorização / SEFAZ */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Status do Documento Fiscal
          </div>
          <div>
            <Badge
              bg={visual.badgeBg}
              text={visual.badgeText}
              className="px-3 py-2 fs-6 shadow-sm"
            >
              <span>{visual.iconSymbol} </span>
              {visual.label}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Número MDF-e</span>
              <strong className="fs-5 text-body">{mdfe.numeroMDFe}</strong>
            </Col>
            <Col xs={12} sm={6} md={2} className="mb-2">
              <span className="text-body-secondary small d-block">Série</span>
              <strong className="fs-5 text-body">{mdfe.serie}</strong>
            </Col>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Data/Hora Emissão</span>
              <strong className="text-body">
                {mdfe.emissaoDataHora
                  ? code.FormatarDataHoraDefault(mdfe.emissaoDataHora)
                  : "-"}
              </strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Status de Envio</span>
              <span className="text-body">
                {codigoStatusEnvio} - {code.getDescricaoStatusEnvioMDFe(codigoStatusEnvio)}
              </span>
            </Col>
          </Row>

          {mdfe.autorizacao && (
            <Row className="mt-2 pt-2 border-top">
              {mdfe.autorizacao.codigoStatusResposta && (
                <Col xs={12} md={6} className="mb-2">
                  <span className="text-body-secondary small d-block">Retorno SEFAZ</span>
                  <span className="text-body fw-medium">
                    {mdfe.autorizacao.codigoStatusResposta} -{" "}
                    {mdfe.autorizacao.descricaoMotivoResposta || "Autorizado"}
                  </span>
                </Col>
              )}
              {mdfe.autorizacao.numeroProtocolo && (
                <Col xs={12} md={3} className="mb-2">
                  <span className="text-body-secondary small d-block">Protocolo</span>
                  <span className="text-body">{mdfe.autorizacao.numeroProtocolo}</span>
                </Col>
              )}
              {mdfe.autorizacao.recebimentoDataHora && (
                <Col xs={12} md={3} className="mb-2">
                  <span className="text-body-secondary small d-block">Data Recebimento</span>
                  <span className="text-body">
                    {code.FormatarDataHoraDefault(mdfe.autorizacao.recebimentoDataHora)}
                  </span>
                </Col>
              )}
              {mdfe.autorizacao.chaveAcesso && (
                <Col xs={12} className="mb-1">
                  <span className="text-body-secondary small d-block">Chave de Acesso</span>
                  <code className="text-primary fs-7">{mdfe.autorizacao.chaveAcesso}</code>
                </Col>
              )}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* 2. Características e Modal do Transporte */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faTruck} className="me-2" />
            Características e Transporte
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Tipo de Emitente</span>
              <strong className="text-body">{code.getTipoEmitenteLabel(mdfe.tipoEmitente)}</strong>
            </Col>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Tipo de Transportador</span>
              <strong className="text-body">{code.getTipoTransportadorLabel(mdfe.tipoTransportador)}</strong>
            </Col>
            <Col xs={12} sm={6} md={2} className="mb-2">
              <span className="text-body-secondary small d-block">Modal</span>
              <strong className="text-body">{code.getModalLabel(mdfe.modal)}</strong>
            </Col>
            <Col xs={12} sm={6} md={2} className="mb-2">
              <span className="text-body-secondary small d-block">RNTRC</span>
              <span className="text-body">{mdfe.rntrc || "-"}</span>
            </Col>
            <Col xs={12} sm={6} md={2} className="mb-2">
              <span className="text-body-secondary small d-block">Previsão Viagem</span>
              <span className="text-body">
                {mdfe.previsaoInicioViagemDataHora
                  ? code.FormatarDataHoraDefault(mdfe.previsaoInicioViagemDataHora)
                  : "-"}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 3. Veículo de Tração e Condutores */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faIdCard} className="me-2" />
            Veículo de Tração e Condutores
          </div>
        </Card.Header>
        <Card.Body>
          <h6 className="fw-bold text-secondary mb-3">Veículo de Tração</h6>
          <Row className="mb-3">
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Placa / UF</span>
              <strong className="text-body fs-6">
                {mdfe.placaVeiculoTracao} - {mdfe.ufVeiculoTracao || "-"}
              </strong>
            </Col>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Tipo de Rodado</span>
              <span className="text-body">{code.getTipoRodadoLabel(mdfe.tipoRodadoVeiculoTracao)}</span>
            </Col>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Tipo de Carroceria</span>
              <span className="text-body">{code.getTipoCarroceriaLabel(mdfe.tipoCarroceriaVeiculoTracao)}</span>
            </Col>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Tara / Cap. (KG / M3)</span>
              <span className="text-body">
                {mdfe.taraVeiculoTracao || 0} kg / {mdfe.capacidadeKGVeiculoTracao || 0} kg ({mdfe.capacidadeM3VeiculoTracao || 0} m³)
              </span>
            </Col>
          </Row>

          {/* Proprietário do Veículo (se terceiro) */}
          {(mdfe.proprietarioVeiculoTracao || mdfe.nomeProprietarioVeiculoTracao) && (
            <div className="p-3 bg-body-tertiary rounded border mb-3">
              <span className="fw-bold text-body-secondary small d-block mb-2">Proprietário do Veículo</span>
              <Row>
                <Col xs={12} md={4} className="mb-1">
                  <span className="text-body-secondary small d-block">Nome / Razão Social</span>
                  <strong className="text-body">
                    {mdfe.proprietarioVeiculoTracao?.nome || mdfe.nomeProprietarioVeiculoTracao || "-"}
                  </strong>
                </Col>
                <Col xs={12} md={3} className="mb-1">
                  <span className="text-body-secondary small d-block">CPF / CNPJ</span>
                  <span className="text-body">
                    {code.formatarDoc(
                      mdfe.proprietarioVeiculoTracao?.documento ||
                        mdfe.proprietarioVeiculoTracao?.cnpj ||
                        mdfe.proprietarioVeiculoTracao?.cpf ||
                        mdfe.documentoProprietarioVeiculoTracao ||
                        mdfe.cnpjProprietarioVeiculoTracao ||
                        mdfe.cpfProprietarioVeiculoTracao
                    )}
                  </span>
                </Col>
                <Col xs={12} md={2} className="mb-1">
                  <span className="text-body-secondary small d-block">RNTRC</span>
                  <span className="text-body">
                    {mdfe.proprietarioVeiculoTracao?.rntrc || mdfe.rntrcProprietarioVeiculoTracao || "-"}
                  </span>
                </Col>
                <Col xs={12} md={3} className="mb-1">
                  <span className="text-body-secondary small d-block">Tipo Proprietário</span>
                  <span className="text-body">
                    {code.getTipoProprietarioLabel(
                      mdfe.proprietarioVeiculoTracao?.tipoProprietario ?? mdfe.tipoProprietarioVeiculoTracao
                    )}
                  </span>
                </Col>
              </Row>
            </div>
          )}

          {/* Condutores */}
          <h6 className="fw-bold text-secondary mt-3 mb-2">Condutores</h6>
          {mdfe.condutores && mdfe.condutores.length > 0 ? (
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nome do Condutor</th>
                  <th>CPF</th>
                </tr>
              </thead>
              <tbody>
                {mdfe.condutores.map((cond, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td className="fw-semibold">{cond.nomeCondutor}</td>
                    <td>{code.formatarDoc(cond.cpfCondutor)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted small">Nenhum condutor informado.</div>
          )}

          {/* Reboques vinculados */}
          {mdfe.reboques && mdfe.reboques.length > 0 && (
            <>
              <h6 className="fw-bold text-secondary mt-4 mb-2">Reboques / Carretas</h6>
              <Table responsive hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Placa / UF</th>
                    <th>Carroceria</th>
                    <th>Tara</th>
                    <th>Capacidade (KG / M3)</th>
                  </tr>
                </thead>
                <tbody>
                  {mdfe.reboques.map((reb, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td className="fw-semibold">
                        {reb.placa} - {reb.uf || "-"}
                      </td>
                      <td>{code.getTipoCarroceriaLabel(reb.tipoCarroceria)}</td>
                      <td>{reb.tara || 0} kg</td>
                      <td>
                        {reb.capacidadeKG || 0} kg / {reb.capacidadeM3 || 0} m³
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      {/* 4. Percurso e Municípios de Carregamento */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faRoute} className="me-2" />
            Percurso e Carregamento
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col xs={12} md={4}>
              <span className="text-body-secondary small d-block">UF de Início</span>
              <strong className="text-body fs-6">{mdfe.ufInicio}</strong>
            </Col>
            <Col xs={12} md={4}>
              <span className="text-body-secondary small d-block">UF de Fim</span>
              <strong className="text-body fs-6">{mdfe.ufFim}</strong>
            </Col>
            <Col xs={12} md={4}>
              <span className="text-body-secondary small d-block">Percurso (UFs Intermediárias)</span>
              <span className="text-body">
                {mdfe.percursos && mdfe.percursos.length > 0
                  ? mdfe.percursos.map((p) => p.ufPercurso).join(" ➔ ")
                  : "Nenhuma UF intermediária"}
              </span>
            </Col>
          </Row>

          <h6 className="fw-bold text-secondary mt-3 mb-2">Municípios de Carregamento</h6>
          {mdfe.municipiosCarregamento && mdfe.municipiosCarregamento.length > 0 ? (
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Código IBGE</th>
                  <th>Município</th>
                </tr>
              </thead>
              <tbody>
                {mdfe.municipiosCarregamento.map((mun, idx) => (
                  <tr key={idx}>
                    <td>{mun.codigoMunicipioCarregamento}</td>
                    <td className="fw-semibold">{mun.nomeMunicipioCarregamento}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted small">Nenhum município de carregamento informado.</div>
          )}
        </Card.Body>
      </Card>

      {/* 5. Municípios de Descarregamento e Documentos Vinculados */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
            Descarregamento e Documentos Vinculados
          </div>
        </Card.Header>
        <Card.Body>
          {mdfe.municipiosDescarga && mdfe.municipiosDescarga.length > 0 ? (
            mdfe.municipiosDescarga.map((desc, idx) => (
              <div key={idx} className="mb-3 p-3 bg-body-tertiary rounded border">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold text-primary">
                    Descarregamento: {desc.nomeMunicipioDescarga} ({desc.codigoMunicipioDescarga})
                  </span>
                  <Badge bg="primary">
                    {desc.documentos?.length || 0} documento(s)
                  </Badge>
                </div>
                {desc.documentos && desc.documentos.length > 0 ? (
                  <Table responsive hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Chave de Acesso</th>
                        <th>Segundo Cód. Barras</th>
                      </tr>
                    </thead>
                    <tbody>
                      {desc.documentos.map((doc, dIdx) => (
                        <tr key={dIdx}>
                          <td>
                            <Badge bg={Number(doc.tipoDocumento) === 1 ? "info" : "success"}>
                              {Number(doc.tipoDocumento) === 1 ? "CT-e" : "NF-e"}
                            </Badge>
                          </td>
                          <td>
                            <code className="text-body fs-7">{doc.chaveAcesso}</code>
                          </td>
                          <td>{doc.segundoCodigoBarras || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-muted small">Nenhum documento anexado neste município.</div>
                )}
              </div>
            ))
          ) : (
            <div className="text-muted small">Nenhum município de descarregamento informado.</div>
          )}
        </Card.Body>
      </Card>

      {/* 6. Totais da Carga e Produto Predominante */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faBoxes} className="me-2" />
            Totais da Carga e Produto Predominante
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Valor Total da Carga</span>
              <strong className="fs-5 text-primary">R$ {valorCargaFormatado}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Peso Bruto Total</span>
              <strong className="fs-5 text-body">
                {mdfe.pesoBrutoTotal || 0} {Number(mdfe.unidadeMedidaPeso) === 2 ? "TON" : "KG"}
              </strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Quantidade de Documentos</span>
              <span className="text-body">
                CT-e: {mdfe.quantidadeCTe || 0} | NF-e: {mdfe.quantidadeNFe || 0}
              </span>
            </Col>
          </Row>

          {mdfe.produtoPredominante && (
            <div className="p-3 bg-body-tertiary rounded border">
              <span className="fw-bold text-body-secondary small d-block mb-2">Produto Predominante</span>
              <Row>
                <Col xs={12} md={4} className="mb-1">
                  <span className="text-body-secondary small d-block">Nome do Produto</span>
                  <strong className="text-body">{mdfe.produtoPredominante.nomeProduto}</strong>
                </Col>
                <Col xs={12} md={3} className="mb-1">
                  <span className="text-body-secondary small d-block">Tipo de Carga</span>
                  <span className="text-body">
                    {code.getTipoCargaLabel(mdfe.produtoPredominante.tipoCarga)}
                  </span>
                </Col>
                <Col xs={12} md={2} className="mb-1">
                  <span className="text-body-secondary small d-block">NCM</span>
                  <span className="text-body">{mdfe.produtoPredominante.ncm || "-"}</span>
                </Col>
                <Col xs={12} md={3} className="mb-1">
                  <span className="text-body-secondary small d-block">CEP Origem / Destino</span>
                  <span className="text-body">
                    {mdfe.produtoPredominante.cepCarregamento || "-"} ➔ {mdfe.produtoPredominante.cepDescarregamento || "-"}
                  </span>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* 7. Seguros */}
      {mdfe.seguros && mdfe.seguros.length > 0 && (
        <Card className="mb-4 border shadow-sm">
          <Card.Header className="bg-body-tertiary py-2">
            <div className="fw-semibold text-primary d-flex align-items-center">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              Seguros da Carga
            </div>
          </Card.Header>
          <Card.Body>
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Responsável</th>
                  <th>Seguradora</th>
                  <th>CNPJ Seguradora</th>
                  <th>Apólice</th>
                  <th>Averbação</th>
                </tr>
              </thead>
              <tbody>
                {mdfe.seguros.map((seg, idx) => (
                  <tr key={idx}>
                    <td>
                      {Number(seg.responsavelSeguro) === 1
                        ? "1 - Emitente MDF-e"
                        : "2 - Responsável Contratação"}
                    </td>
                    <td className="fw-semibold">{seg.nomeSeguradora || "-"}</td>
                    <td>{code.formatarDoc(seg.cnpjSeguradora)}</td>
                    <td>{seg.numeroApolice || "-"}</td>
                    <td>{seg.numeroAverbaCorrente || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* 8. Contratantes, CIOTs e Vales-Pedágio */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faUsers} className="me-2" />
            Contratantes, CIOTs e Vales-Pedágio
          </div>
        </Card.Header>
        <Card.Body>
          {/* Contratantes */}
          <h6 className="fw-bold text-secondary mb-2">Contratantes do Frete</h6>
          {mdfe.contratantes && mdfe.contratantes.length > 0 ? (
            <Table responsive hover size="sm" className="mb-3">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nome / Razão Social</th>
                  <th>CPF / CNPJ</th>
                  <th>ID Estrangeiro</th>
                </tr>
              </thead>
              <tbody>
                {mdfe.contratantes.map((c, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td className="fw-semibold">{c.nomeContratante || "-"}</td>
                    <td>{code.formatarDoc(c.documento || c.cnpj || c.cpf)}</td>
                    <td>{c.idEstrangeiro || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted small mb-3">Nenhum contratante informado.</div>
          )}

          {/* CIOTs */}
          <h6 className="fw-bold text-secondary mb-2">CIOTs (ANTT)</h6>
          {((mdfe.ciots && mdfe.ciots.length > 0) || (mdfe.cioTs && mdfe.cioTs.length > 0)) ? (
            <Table responsive hover size="sm" className="mb-3">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Número CIOT</th>
                  <th>CPF / CNPJ</th>
                </tr>
              </thead>
              <tbody>
                {(mdfe.ciots || mdfe.cioTs || []).map((ciot, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td className="fw-semibold">{ciot.ciot}</td>
                    <td>{code.formatarDoc(ciot.documento || ciot.cnpj || ciot.cpf)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted small mb-3">Nenhum CIOT informado.</div>
          )}

          {/* Vales-Pedágio */}
          <h6 className="fw-bold text-secondary mb-2">Vales-Pedágio</h6>
          {mdfe.valesPedagio && mdfe.valesPedagio.length > 0 ? (
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fornecedora (CNPJ)</th>
                  <th>Responsável Pagamento</th>
                  <th>Nº Comprovante</th>
                  <th>Valor</th>
                  <th>Tipo</th>
                  <th>Categoria Combinação</th>
                </tr>
              </thead>
              <tbody>
                {mdfe.valesPedagio.map((vale, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      {vale.nomeFornecedoraValePedagio && (
                        <span className="d-block fw-semibold">{vale.nomeFornecedoraValePedagio}</span>
                      )}
                      <span>{code.formatarDoc(vale.cnpjEmitenteValePedagio)}</span>
                    </td>
                    <td>
                      {vale.nomeResponsavelPagamento && (
                        <span className="d-block fw-semibold">{vale.nomeResponsavelPagamento}</span>
                      )}
                      <span>
                        {code.formatarDoc(
                          vale.documentoResponsavelPagamento ||
                            vale.cnpjResponsavelPagamento ||
                            vale.cpfResponsavelPagamento
                        )}
                      </span>
                    </td>
                    <td>{vale.numeroComprovante}</td>
                    <td className="fw-semibold text-primary">
                      R$ {Number(vale.valorValePedagio || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td>{code.getTipoValePedagioLabel(vale.tipoValePedagio)}</td>
                    <td>
                      {code.obterDescricaoCategoriaCombinacaoVeicular(
                        vale.categoriaCombinacaoVeicular
                      ) || vale.categoriaCombinacaoVeicular || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted small">Nenhum vale-pedágio vinculado.</div>
          )}
        </Card.Body>
      </Card>

      {/* 9. Informações de Pagamento do Frete */}
      {mdfe.pagamentos && mdfe.pagamentos.length > 0 && (
        <Card className="mb-4 border shadow-sm">
          <Card.Header className="bg-body-tertiary py-2">
            <div className="fw-semibold text-primary d-flex align-items-center">
              <FontAwesomeIcon icon={faDollarSign} className="me-2" />
              Pagamento do Frete
            </div>
          </Card.Header>
          <Card.Body>
            {mdfe.pagamentos.map((pag, idx) => (
              <div key={idx} className="mb-3 p-3 bg-body-tertiary rounded border">
                <Row className="mb-2">
                  <Col xs={12} md={4}>
                    <span className="text-body-secondary small d-block">Forma de Pagamento</span>
                    <strong className="text-body">
                      {Number(pag.formaPagamento) === 1 ? "1 - A Prazo" : "0 - À Vista"}
                    </strong>
                  </Col>
                  <Col xs={12} md={4}>
                    <span className="text-body-secondary small d-block">Valor Total Contrato</span>
                    <strong className="text-primary fs-6">
                      R$ {Number(pag.valorTotalContrato || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </Col>
                  <Col xs={12} md={4}>
                    <span className="text-body-secondary small d-block">Beneficiário</span>
                    <span className="text-body">
                      {pag.nomeBeneficiario ? `${pag.nomeBeneficiario} - ` : ""}
                      {code.formatarDoc(pag.cnpjBeneficiario || pag.cpfBeneficiario)}
                    </span>
                  </Col>
                </Row>

                {/* Dados Bancários */}
                {(pag.codigoBanco || pag.chavePixBancario || pag.indicadorPagamentoBancario) && (
                  <div className="p-2 bg-body rounded border mb-2 small">
                    <span className="fw-bold text-body-secondary d-block mb-1">Dados Bancários / Meio</span>
                    <Row>
                      {pag.codigoBanco && (
                        <Col xs={12} md={4}>
                          Banco COMPE: <strong>{pag.codigoBanco}</strong> {code.obterNomeBancoCompe(pag.codigoBanco) ? `(${code.obterNomeBancoCompe(pag.codigoBanco)})` : ""}
                        </Col>
                      )}
                      {pag.codigoAgencia && (
                        <Col xs={12} md={3}>
                          Agência: <strong>{pag.codigoAgencia}</strong>
                        </Col>
                      )}
                      {pag.chavePixBancario && (
                        <Col xs={12} md={5}>
                          Chave PIX: <strong>{pag.chavePixBancario}</strong>
                        </Col>
                      )}
                    </Row>
                  </div>
                )}

                {/* Componentes */}
                {pag.componentes && pag.componentes.length > 0 && (
                  <div className="mt-2">
                    <span className="fw-semibold text-secondary small d-block mb-1">Componentes do Pagamento:</span>
                    <Table responsive hover size="sm" className="mb-2">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Descrição</th>
                          <th>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pag.componentes.map((comp, cIdx) => (
                          <tr key={cIdx}>
                            <td>{code.getTipoComponentePagamentoLabel(comp.tipoComponente)}</td>
                            <td>{comp.descricaoOutro || "-"}</td>
                            <td className="fw-semibold">
                              R$ {Number(comp.valorComponente || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Parcelas */}
                {pag.parcelas && pag.parcelas.length > 0 && (
                  <div className="mt-2">
                    <span className="fw-semibold text-secondary small d-block mb-1">Parcelas:</span>
                    <Table responsive hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Parcela</th>
                          <th>Vencimento</th>
                          <th>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pag.parcelas.map((parc, pIdx) => (
                          <tr key={pIdx}>
                            <td>#{parc.numeroParcela || pIdx + 1}</td>
                            <td>{parc.dataVencimento ? code.FormatarDataHoraDefault(parc.dataVencimento).split(" ")[0] : "-"}</td>
                            <td className="fw-semibold">
                              R$ {Number(parc.valorParcela || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {/* 10. Informações Adicionais */}
      {(mdfe.informacoesAdicionaisFisco || mdfe.informacoesComplementaresContribuinte) && (
        <Card className="mb-4 border shadow-sm">
          <Card.Header className="bg-body-tertiary py-2">
            <div className="fw-semibold text-primary d-flex align-items-center">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Informações Adicionais
            </div>
          </Card.Header>
          <Card.Body>
            {mdfe.informacoesAdicionaisFisco && (
              <div className="mb-3">
                <span className="text-body-secondary small fw-bold d-block">
                  Informações de Interesse do Fisco:
                </span>
                <p className="text-body mb-0 bg-body-tertiary p-2 rounded border">
                  {mdfe.informacoesAdicionaisFisco}
                </p>
              </div>
            )}
            {mdfe.informacoesComplementaresContribuinte && (
              <div>
                <span className="text-body-secondary small fw-bold d-block">
                  Informações Complementares de Interesse do Contribuinte:
                </span>
                <p className="text-body mb-0 bg-body-tertiary p-2 rounded border">
                  {mdfe.informacoesComplementaresContribuinte}
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default MDFeVisualizaView;
